/**
 * Product Search Results
 * ------------------------------------------------------------------
 * Queries Boost AI Search & Discovery's public search API directly
 * (https://services.mybcapps.com/bc-sf-filter/search) and renders a
 * paginated, filterable, sortable grid of matching products into
 * this CMAX page, independent of the theme's own /search route.
 *
 * full_min_match=true is passed on every request so a query like
 * "1200 Vanity" requires products to match all words, not just any
 * one of them.
 *
 * Filters come from Boost's filter tree (build_filter_tree=true on
 * every request, since Boost recalculates doc_count per current
 * refinement). Checkbox/radio (list-type) filters are verified
 * working: Boost requires "[]" appended to the filterOptionId
 * (?pf_v_brand[]=Aulic&pf_v_brand[]=Fienza) — the bare id from
 * Boost's own docs example does NOT filter anything (tested live:
 * total_product stayed unchanged without the brackets). Range-type
 * options (price) are NOT sent to Boost — none of the conventions
 * tried against the live API (pf_p_price as CSV, as a bracketed
 * array, _min/_max, _from/_to, _gte/_lte, dash-separated) changed
 * total_product or returned prices outside the slider's range.
 * Instead, price range is applied client-side: the current page's
 * products are fetched unfiltered and then filtered by price_min
 * in-browser before rendering (see applyPriceRange() below). This
 * only filters within the current page of server results, not the
 * full catalogue — total_product/pagination still reflect Boost's
 * unfiltered count. If Boost support ever confirms the real
 * server-side param, buildUrl() should send it instead.
 *
 * Cards use the same class names as snippets/product-item.liquid, and
 * the filter sidebar/drawer/toolbar reuse the exact classes from
 * sections/main-search.liquid, so this matches the theme's
 * collection/search design. Two pieces of that markup are real
 * globally-registered theme.js behavior and need no JS from us:
 *  - [data-action="toggle-collapsible"] accordions (CollapsibleManager
 *    delegates from document.body).
 *  - <price-range> sliders (a custom element registered with
 *    customElements.define, auto-upgrades wherever it appears).
 * Everything else here (mobile drawer open/close, sort dropdown
 * open/close/select, checkbox/price-range change handling) is
 * reimplemented locally, because the theme's own classes for those
 * (CollectionFilterDrawer, ValuePicker, CollectionTemplate) are only
 * instantiated by the real main-search/main-collection sections and
 * can't attach to this page.
 *
 * Not replicated (kept out of scope): grid/list view toggle, quick
 * view/quick buy, product labels/badges, swatch/image filter
 * presentations (rendered as plain checkbox lists instead), numbered
 * pagination (kept as simple Previous/Next).
 */
(function () {
  'use strict';

  var PAGE_SIZE = 24;
  var GRID_CLASSES = '1/2--tablet-and-up 1/4--desk';

  function qs(selector, context) {
    return (context || document).querySelector(selector);
  }

  function qsa(selector, context) {
    return Array.prototype.slice.call((context || document).querySelectorAll(selector));
  }

  function escapeHtml(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function escapeAttr(value) {
    return escapeHtml(value).replace(/"/g, '&quot;');
  }

  function formatMoney(amount, currency) {
    // ponytail: locale pinned to en-AU (not navigator.language) so AUD always
    // renders as "$", never "A$" — Intl adds the "A" to disambiguate AUD from
    // the visitor's own locale currency, which the rest of the site never does.
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency || 'AUD'
      }).format(amount);
    } catch (e) {
      return '$' + Number(amount).toFixed(2);
    }
  }

  function firstImage(product) {
    if (product.images_info && product.images_info.length) return product.images_info[0].src;
    if (product.images) {
      var keys = Object.keys(product.images);
      if (keys.length) return product.images[keys[0]];
    }
    return null;
  }

  // Hover swap image — mirrors product.images[1] in snippets/product-item.liquid
  function secondImage(product) {
    var src = product.images_info && product.images_info[1] && product.images_info[1].src;
    return src && src !== firstImage(product) ? src : null;
  }

  // Same bag icon as the Liquid card's ATC button
  var BAG_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>';
  var ETA_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2.5 2M9 2h6"/></svg>';
  var ENQUIRE_ICON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/></svg>';
  var showStockStatus = true;
  var isB2B = false;
  var tradePriceLabelText = 'Trade Price';
  var leadFreeBadgeUrl = '';
  var discountMode = ''; // '' (off) | 'percentage' | 'saving' — theme settings.discount_mode
  var discountLabel = 'Save %s';

  // Mirrors snippets/stock-status-label.liquid's 'inline' mode (A2 card meta row)
  function stockStatusMarkup(product, variant) {
    if (!showStockStatus || !variant) return '';
    if (!variant.available) return '<span class="stock-inline stock-inline--sold">Sold out</span>';
    var tracked = variant.inventory_management === 'shopify';
    if (!tracked || variant.inventory_quantity > 0) return '<span class="stock-inline stock-inline--ready">Ready to Ship</span>';
    if (metafieldTrue(product, 'custom', 'enquire_for_eta')) return '<span class="stock-inline stock-inline--enquire">' + ENQUIRE_ICON + '<span>Enquire for ETA</span></span>';
    return '<span class="stock-inline stock-inline--eta">' + ETA_ICON + '<span>Ships in <span class="stock-status-label__approx">approx. </span>2 business days</span></span>';
  }

  function metafieldValue(product, namespace, key) {
    var list = Array.isArray(product.metafields) ? product.metafields : [];
    for (var i = 0; i < list.length; i++) {
      if (list[i] && list[i].namespace === namespace && list[i].key === key) return list[i].value;
    }
    return null;
  }

  function metafieldTrue(product, namespace, key) {
    return metafieldValue(product, namespace, key) === 'true';
  }

  function inCollection(product, handle) {
    var cols = Array.isArray(product.collections) ? product.collections : [];
    return cols.some(function (c) { return c && c.handle === handle; });
  }

  // Mirrors snippets/product-item.liquid's label list, rebuilt from Boost's
  // search payload (metafields[], collections[], tags[], published_at). The
  // custom.* / c_f.* metafield badges only appear once those metafield
  // definitions are enabled in Boost SD's field sync — until then the payload
  // omits them and those branches are simply skipped.
  function labelsMarkup(product, currency) {
    var tags = (Array.isArray(product.tags) ? product.tags : []).map(String);
    if (tags.some(function (t) { return t.toLowerCase() === 'hide-price'; })) return '';

    var labels = [];
    function pill(text, cls) {
      labels.push('<span class="product-label ' + cls + '">' + escapeHtml(text) + '</span>');
    }

    // Metafield custom badges (custom.badges — list or single value)
    var badges = metafieldValue(product, 'custom', 'badges');
    if (badges) {
      var parsed;
      try { parsed = JSON.parse(badges); } catch (e) { parsed = badges; }
      (Array.isArray(parsed) ? parsed : [parsed]).forEach(function (b) {
        if (b != null && String(b) !== '') pill(String(b), 'product-label--custom1');
      });
    }

    // Tag-based labels
    tags.forEach(function (tag) {
      if (tag.indexOf('__label:') === 0) pill(tag.slice(8), 'product-label--custom1');
      else if (tag.indexOf('__label1:') === 0) pill(tag.slice(9), 'product-label--custom1');
      else if (tag.indexOf('__label2:') === 0) pill(tag.slice(9), 'product-label--custom2');
    });

    // New badge — 'new' tag or <30 days since published
    var isNew = tags.some(function (t) { return t.toLowerCase() === 'new'; });
    if (!isNew && product.published_at) {
      var days = (Date.now() - new Date(product.published_at).getTime()) / 86400000;
      if (days >= 0 && days < 30) isNew = true;
    }
    if (isNew) labels.push('<span class="product-label product-label--new" aria-hidden="true">New</span>');

    // Lead free
    if (metafieldTrue(product, 'custom', 'on_lead_free')) {
      if (leadFreeBadgeUrl) {
        labels.push('<img src="' + escapeAttr(leadFreeBadgeUrl) + '" loading="lazy" alt="Lead Free" class="product-label product-label--image">');
      } else {
        pill('Lead Free', 'product-label--custom2');
      }
    }

    // Sale badge — same settings + locale string as snippets/product-item.liquid
    if (discountMode && product.compare_at_price_min && product.price_min != null && product.compare_at_price_min > product.price_min) {
      var diff = product.compare_at_price_min - product.price_min;
      var savings = discountMode === 'percentage'
        ? Math.round(diff * 100 / product.compare_at_price_min) + '%'
        : formatMoney(diff, currency);
      pill(discountLabel.replace('%s', savings), 'product-label--on-sale');
    }

    // Bundle (metafield c_f.grouped_sub_product)
    var grouped = metafieldValue(product, 'c_f', 'grouped_sub_product');
    if (grouped && grouped !== 'false') {
      labels.push('<span class="product-label product-label--bundle" aria-hidden="true">Bundle</span>');
    }

    // Collection-membership badges — consolidated when both clearance and final stock apply
    var isClearance = inCollection(product, 'clearance-stock');
    var isFinalStock = metafieldTrue(product, 'custom', 'on_final_stock');
    if (isClearance && isFinalStock) {
      pill('Clearance – Final Stock', 'product-label--clearance');
    } else if (isClearance) {
      pill('Clearance Stock', 'product-label--clearance');
    } else if (isFinalStock) {
      pill('Final Stock', 'product-label--clearance');
    }

    if (!labels.length) return '';
    return '<div class="product-item__label-list">' + labels.join('') + '</div>';
  }

  // Shown when a product has no image, matching the Liquid card's placeholder tile.
  var PLACEHOLDER_SVG =
    '<svg class="product-item__placeholder" viewBox="0 0 24 24" width="34%" height="34%" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color: #b9c3cc;" aria-hidden="true">' +
    '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="m21 16-5-5-9 9"/></svg>';

  function cardMarkup(product, currency) {
    var image = firstImage(product);
    var hoverImage = image && secondImage(product);
    var url = '/products/' + encodeURIComponent(product.handle);
    var priceHtml = '';

    if (product.price_min != null) {
      priceHtml = '<span class="price price--current">' + escapeHtml(formatMoney(product.price_min, currency)) + '</span>';
      if (product.price_max != null && product.price_max > product.price_min) {
        priceHtml = '<span class="price price--current">' +
          escapeHtml(formatMoney(product.price_min, currency)) + ' &ndash; ' + escapeHtml(formatMoney(product.price_max, currency)) +
          '</span>';
      } else if (product.compare_at_price_min && product.compare_at_price_min > product.price_min) {
        priceHtml += '<span class="price price--compare"><s>' + escapeHtml(formatMoney(product.compare_at_price_min, currency)) + '</s></span>';
      }
    }

    var firstVariant = product.variants && product.variants[0];
    var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase(); });
    var hidePrice = tags.indexOf('hide-price') !== -1;
    var quickAdd = '';
    if (hidePrice) {
      quickAdd = '<a href="' + url + '" class="button product-item__quick-add-btn">View Details</a>';
    } else if (firstVariant && product.available) {
      quickAdd =
        '<button type="button" class="button product-item__quick-add-btn" data-product-search-add-to-cart data-variant-id="' + escapeAttr(firstVariant.id) + '">' +
        BAG_ICON + '<span class="product-item__atc-text">Add to cart</span><span class="product-item__atc-plus" aria-hidden="true">+</span>' +
        '</button>';
    }

    return (
      '<div class="product-item product-item--vertical product-item--a2' + (hoverImage ? ' product-item--swappable' : '') + ' ' + GRID_CLASSES + '">' +
      '<div class="product-item__media">' +
      (image
        ? '<a href="' + url + '" class="product-item__image-wrapper">' +
          '<div class="aspect-ratio">' +
          '<img src="' + escapeAttr(image) + '" alt="' + escapeAttr(product.title) + '" loading="lazy" class="product-item__primary-image">' +
          (hoverImage ? '<img src="' + escapeAttr(hoverImage) + '" alt="" loading="lazy" class="product-item__secondary-image">' : '') +
          '</div>' +
          '</a>'
        : '<a href="' + url + '" class="product-item__image-wrapper product-item__image-wrapper--placeholder" aria-label="' + escapeAttr(product.title) + '">' +
          PLACEHOLDER_SVG +
          '</a>') +
      (quickAdd ? '<div data-spark="b2c-only" class="product-item__quick-add">' + quickAdd + '</div>' : '') +
      '</div>' +
      labelsMarkup(product, currency) +
      '<div class="product-item__info">' +
      '<div class="product-item__meta">' +
      (product.vendor ? '<span class="product-item__brand">' + escapeHtml(product.vendor) + '</span>' : '') +
      (hidePrice ? '' : stockStatusMarkup(product, firstVariant)) +
      '</div>' +
      '<div class="product-item__info-inner">' +
      (hidePrice
        ? '<div class="product-item__label product-item__label--custom"><span class="product-label">In-Store Only</span></div>'
        : (isB2B ? '<span class="trade-price-label">' + escapeHtml(tradePriceLabelText) + '</span>' : '') +
          '<div class="product-item__price-list price-list"' + (isB2B ? ' data-spark="b2c-only"' : '') + '>' + priceHtml + '</div>') +
      '</div>' +
      '<a href="' + url + '" class="product-item__title text--strong link">' + escapeHtml(product.title) + '</a>' +
      '</div>' +
      '</div>'
    );
  }

  function safeId(raw) {
    // Boost's filterOptionId values contain "::" (e.g. "pf_m_::custom::availability"),
    // which is invalid inside an HTML id used as a querySelector target (":" starts a
    // CSS pseudo-class) — querySelector("#" + id) throws and silently breaks the
    // CollapsibleManager accordion toggle. Sanitize for DOM ids; the raw filterOptionId
    // is still used as-is for the actual query parameter via data-filter-id.
    return String(raw).replace(/[^a-zA-Z0-9_-]/g, '-');
  }

  function rangeMinMax(option) {
    var source = Array.isArray(option.values) ? option.values[0] : option.values;
    source = source || {};
    return { min: Number(source.min) || 0, max: Number(source.max) || 0 };
  }

  function filterGroupMarkup(option, state, context, currencySymbol) {
    var selected = state.filters[option.filterOptionId] || {};
    var isExpanded = state.expandedGroup === option.filterOptionId;
    var controlsId = safeId(option.filterOptionId) + '--' + context;

    var toggleButton =
      '<button type="button" class="collection__filter-group-name link link--secondary text--strong" data-action="toggle-collapsible" aria-controls="' + escapeAttr(controlsId) + '" data-close-siblings="false" data-collapsible-force-overflow="true" aria-expanded="' + (isExpanded ? 'true' : 'false') + '">' +
      escapeHtml(option.label) +
      '<svg width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true"><path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';

    var body;
    if (option.displayType === 'range') {
      var range = rangeMinMax(option);
      var minValue = state.range[option.filterOptionId] ? state.range[option.filterOptionId].min : range.min;
      var maxValue = state.range[option.filterOptionId] ? state.range[option.filterOptionId].max : range.max;
      var span = range.max - range.min || 1;

      body =
        '<price-range class="price-range" data-filter-range-id="' + escapeAttr(option.filterOptionId) + '">' +
        '<div class="price-range__range-group range-group" style="--range-min: ' + ((minValue - range.min) / span * 100) + '%; --range-max: ' + ((maxValue - range.min) / span * 100) + '%">' +
        '<input type="range" aria-label="' + escapeAttr(option.label) + ' from" class="range" min="' + range.min + '" max="' + range.max + '" value="' + minValue + '">' +
        '<input type="range" aria-label="' + escapeAttr(option.label) + ' to" class="range" min="' + range.min + '" max="' + range.max + '" value="' + maxValue + '">' +
        '</div>' +
        '<div class="price-range__input-group">' +
        '<div class="price-range__input input-prefix">' +
        '<span class="input-prefix__value">' + escapeHtml(currencySymbol) + '</span>' +
        '<input aria-label="' + escapeAttr(option.label) + ' from" class="input-prefix__field" data-filter-range-id="' + escapeAttr(option.filterOptionId) + '" data-filter-range-role="min" type="number" inputmode="numeric" value="' + minValue + '" min="' + range.min + '" max="' + maxValue + '">' +
        '</div>' +
        '<span class="price-range__delimiter text--small">-</span>' +
        '<div class="price-range__input input-prefix">' +
        '<span class="input-prefix__value">' + escapeHtml(currencySymbol) + '</span>' +
        '<input aria-label="' + escapeAttr(option.label) + ' to" class="input-prefix__field" data-filter-range-id="' + escapeAttr(option.filterOptionId) + '" data-filter-range-role="max" type="number" inputmode="numeric" value="' + maxValue + '" min="' + minValue + '" max="' + range.max + '">' +
        '</div>' +
        '</div>' +
        '</price-range>';
    } else {
      var inputType = option.selectType === 'single' ? 'radio' : 'checkbox';
      var values = option.values || [];
      body =
        '<ul class="collection__filter-checkbox-list" role="list">' +
        values.map(function (value, index) {
          var id = safeId(option.filterOptionId) + '-' + index + '--' + context;
          var isChecked = !!selected[value.key];
          return (
            '<li class="collection__filter-checkbox">' +
            '<div class="checkbox-wrapper">' +
            '<input type="' + inputType + '" class="checkbox" id="' + escapeAttr(id) + '" name="' + escapeAttr(option.filterOptionId) + '--' + context + '" data-filter-id="' + escapeAttr(option.filterOptionId) + '" data-filter-value="' + escapeAttr(value.key) + '" ' + (isChecked ? 'checked' : '') + '>' +
            '<svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
            '</div>' +
            '<label for="' + escapeAttr(id) + '">' + escapeHtml(value.label || value.key) + '</label>' +
            '</li>'
          );
        }).join('') +
        '</ul>';
    }

    return (
      '<div class="collection__filter-group">' +
      toggleButton +
      '<div id="' + escapeAttr(controlsId) + '" class="collection__filter-collapsible" ' + (isExpanded ? 'aria-hidden="false" style="height: auto; overflow: visible"' : 'aria-hidden="true"') + '>' +
      body +
      '</div>' +
      '</div>'
    );
  }

  function activeChipsMarkup(filterTree, state) {
    var chips = [];

    filterTree.forEach(function (option) {
      if (option.displayType === 'range') {
        var r = state.range[option.filterOptionId];
        if (r) chips.push({ label: escapeHtml(option.label) + ': ' + r.min + '-' + r.max, filterId: option.filterOptionId, isRange: true });
        return;
      }
      var selected = state.filters[option.filterOptionId] || {};
      Object.keys(selected).forEach(function (key) {
        if (!selected[key]) return;
        var valueObj = (option.values || []).filter(function (v) { return v.key === key; })[0];
        chips.push({ label: escapeHtml((valueObj && valueObj.label) || key), filterId: option.filterOptionId, value: key });
      });
    });

    if (!chips.length) return '';

    return chips.map(function (chip) {
      return (
        '<button type="button" class="collection__active-filter-item" data-clear-chip data-filter-id="' + escapeAttr(chip.filterId) + '" ' + (chip.isRange ? 'data-filter-range="true"' : 'data-filter-value="' + escapeAttr(chip.value) + '"') + '>' +
        '<span class="collection__active-filter-cross"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>' +
        chip.label +
        '</button>'
      );
    }).join('') + '<button type="button" class="collection__clear-filter button button--small button--transparent button--full" data-action="clear-filters">Clear all</button>';
  }

  function init(container) {
    var searchQuery = container.getAttribute('data-search-query') || '';
    var shopDomain = container.getAttribute('data-shop-domain') || '';
    var currency = container.getAttribute('data-currency') || 'AUD';
    var currencySymbol = container.getAttribute('data-currency-symbol') || '$';
    showStockStatus = container.getAttribute('data-show-stock-status') === 'true';
    isB2B = container.getAttribute('data-b2b') === 'true';
    tradePriceLabelText = container.getAttribute('data-trade-price-label') || 'Trade Price';
    leadFreeBadgeUrl = container.getAttribute('data-lead-free-badge') || '';
    discountMode = container.getAttribute('data-discount-mode') || '';
    discountLabel = container.getAttribute('data-discount-label') || 'Save %s';
    var drawerId = container.getAttribute('data-drawer-id');
    var sortId = container.getAttribute('data-sort-id');

    var loadingEl = qs('[data-product-search-results-loading]', container);
    var layoutEl = qs('[data-product-search-results-layout]', container);
    var contentEl = qs('[data-product-search-results-content]', container);
    var paginationEl = qs('[data-product-search-results-pagination]', container);
    var countEl = qs('[data-product-search-results-count]', container);
    var filterCountEl = qs('[data-product-search-results-filter-count]', container);
    var drawerCountEl = qs('[data-product-search-results-drawer-count]', container);
    var drawerClearBtn = qs('[data-product-search-results-clear-all]', container);
    var sortLabelEl = qs('[data-product-search-results-sort-label]', container);
    var filterContainers = qsa('[data-product-search-results-filters]', container);
    var chipsEl = qs('[data-product-search-results-chips]', container);
    var drawerEl = drawerId ? document.getElementById(drawerId) : null;
    var sortPicker = sortId ? document.getElementById(sortId) : null;

    if (!contentEl) return;

    if (!searchQuery || !shopDomain) {
      if (loadingEl) {
        loadingEl.hidden = false;
        loadingEl.textContent = 'No search term is configured for this page yet.';
      }
      return;
    }

    var state = { page: 0, sort: 'relevance', filters: {}, range: {}, expandedGroup: null };
    var currentRequestToken = 0;

    function activeFilterCount() {
      var count = 0;
      Object.keys(state.filters).forEach(function (id) {
        Object.keys(state.filters[id]).forEach(function (key) {
          if (state.filters[id][key]) count += 1;
        });
      });
      count += Object.keys(state.range).length;
      return count;
    }

    function buildUrl() {
      var params = new URLSearchParams();
      params.set('shop', shopDomain);
      params.set('q', searchQuery);
      params.set('page', String(state.page));
      params.set('limit', String(PAGE_SIZE));
      params.set('sort', state.sort);
      params.set('full_min_match', 'true');
      params.set('build_filter_tree', 'true');

      Object.keys(state.filters).forEach(function (id) {
        Object.keys(state.filters[id]).forEach(function (key) {
          if (state.filters[id][key]) params.append(id + '[]', key);
        });
      });
      // NOTE: price range isn't actually applied server-side yet — see the
      // file header comment. Left out of the request entirely rather than
      // sending a param that silently does nothing.

      return 'https://services.mybcapps.com/bc-sf-filter/search?' + params.toString();
    }

    function renderFilters(filterTree) {
      var visibleOptions = filterTree.filter(function (option) {
        return option.displayType === 'range' || (option.values && option.values.length > 0);
      });

      visibleOptions.forEach(function (option, index) {
        if (state.expandedGroup === null && index === 0) state.expandedGroup = option.filterOptionId;
      });

      filterContainers.forEach(function (el) {
        var context = el.getAttribute('data-context');
        el.innerHTML = visibleOptions.map(function (option) {
          return filterGroupMarkup(option, state, context, currencySymbol);
        }).join('');
      });

      if (chipsEl) chipsEl.innerHTML = activeChipsMarkup(filterTree, state);

      var count = activeFilterCount();
      if (filterCountEl) filterCountEl.textContent = count > 0 ? ' (' + count + ')' : '';
      if (drawerCountEl) {
        drawerCountEl.textContent = '(' + count + ')';
        drawerCountEl.style.display = count > 0 ? '' : 'none';
      }
      if (drawerClearBtn) drawerClearBtn.style.display = count > 0 ? '' : 'none';
    }

    function renderPagination(totalProducts) {
      var totalPages = Math.max(1, Math.ceil(totalProducts / PAGE_SIZE));
      if (totalPages <= 1) {
        paginationEl.hidden = true;
        paginationEl.innerHTML = '';
        return;
      }

      paginationEl.hidden = false;
      paginationEl.innerHTML =
        '<button type="button" class="button button--small button--secondary" data-page-prev' + (state.page <= 0 ? ' disabled' : '') + '>Previous</button>' +
        '<span>Page ' + (state.page + 1) + ' of ' + totalPages + '</span>' +
        '<button type="button" class="button button--small button--secondary" data-page-next' + (state.page >= totalPages - 1 ? ' disabled' : '') + '>Next</button>';

      var prevBtn = qs('[data-page-prev]', paginationEl);
      var nextBtn = qs('[data-page-next]', paginationEl);
      if (prevBtn) prevBtn.addEventListener('click', function () { if (state.page > 0) { state.page -= 1; fetchAndRender(); } });
      if (nextBtn) nextBtn.addEventListener('click', function () { if (state.page < totalPages - 1) { state.page += 1; fetchAndRender(); } });
    }

    function applyPriceRange(products) {
      var rangeIds = Object.keys(state.range);
      if (!rangeIds.length) return products;

      return products.filter(function (product) {
        return rangeIds.every(function (id) {
          var r = state.range[id];
          var min = product.price_min;
          var max = product.price_max != null ? product.price_max : product.price_min;
          return max >= r.min && min <= r.max;
        });
      });
    }

    function fetchAndRender() {
      var token = ++currentRequestToken;
      loadingEl.hidden = false;
      contentEl.style.opacity = '0.5';

      fetch(buildUrl(), { credentials: 'omit' })
        .then(function (response) {
          if (!response.ok) throw new Error('Boost search request failed with status ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (token !== currentRequestToken) return;

          var products = applyPriceRange(data.products || []);
          loadingEl.hidden = true;
          layoutEl.hidden = false;
          contentEl.style.opacity = '';

          if (data.filter && data.filter.options) renderFilters(data.filter.options);
          // Price range filters this page's results client-side, so once one is
          // active the server's total_product no longer matches what's shown.
          var hasPriceFilter = Object.keys(state.range).length > 0;
          if (countEl) countEl.textContent = (hasPriceFilter ? products.length : (data.total_product || products.length)) + ' results';

          if (!products.length) {
            contentEl.innerHTML = '<p class="product-search-results__empty">No products found.</p>';
            paginationEl.hidden = true;
            return;
          }

          contentEl.innerHTML =
            '<div class="product-list product-list--collection">' +
            products.map(function (product) { return cardMarkup(product, currency); }).join('') +
            '</div>';

          // Cards are injected after SparkLayer's initial DOM scan, so nudge it to
          // re-scan and swap in trade pricing on the new B2C price elements.
          if (isB2B && window.SparkLayer && typeof window.SparkLayer.refresh === 'function') {
            window.SparkLayer.refresh();
          }

          renderPagination(data.total_product || products.length);
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        })
        .catch(function (err) {
          if (token !== currentRequestToken) return;
          loadingEl.hidden = false;
          loadingEl.textContent = 'Sorry, we could not load products right now. Please try refreshing the page.';
          if (window.console && window.console.error) window.console.error('[product-search-results]', err);
        });
    }

    function clearAllFilters() {
      state.filters = {};
      state.range = {};
      state.page = 0;
      fetchAndRender();
    }

    // Checkbox / radio / price-range filter changes (delegated across both
    // the desktop sidebar and mobile drawer copies of the filter markup).
    filterContainers.forEach(function (el) {
      el.addEventListener('change', function (event) {
        var input = event.target;

        if (input.matches('input[data-filter-id]')) {
          var id = input.getAttribute('data-filter-id');
          var value = input.getAttribute('data-filter-value');
          if (input.type === 'radio') state.filters[id] = {};
          if (!state.filters[id]) state.filters[id] = {};
          state.filters[id][value] = input.checked;
          state.page = 0;
          fetchAndRender();
          return;
        }

        // Price range: the number inputs fire native "change" on blur/Enter, and
        // <price-range>'s own custom element re-dispatches "change" on them when
        // the slider moves, so this one listener covers both interactions.
        if (input.matches('input[data-filter-range-id]')) {
          var rangeId = input.getAttribute('data-filter-range-id');
          var fields = el.querySelectorAll('[data-filter-range-id="' + rangeId + '"][data-filter-range-role]');
          var min = null, max = null;
          fields.forEach(function (field) {
            if (field.getAttribute('data-filter-range-role') === 'min') min = Number(field.value);
            if (field.getAttribute('data-filter-range-role') === 'max') max = Number(field.value);
          });
          if (min == null || max == null) return;
          state.range[rangeId] = { min: min, max: max };
          state.page = 0;
          fetchAndRender();
        }
      });
    });

    // Clear-all (sidebar chip list button + mobile drawer header button) and chip removal
    container.addEventListener('click', function (event) {
      var clearAllBtn = event.target && event.target.closest ? event.target.closest('[data-action="clear-filters"]') : null;
      if (clearAllBtn) { clearAllFilters(); return; }

      var chipBtn = event.target && event.target.closest ? event.target.closest('[data-clear-chip]') : null;
      if (chipBtn) {
        var id = chipBtn.getAttribute('data-filter-id');
        if (chipBtn.getAttribute('data-filter-range') === 'true') {
          delete state.range[id];
        } else if (state.filters[id]) {
          state.filters[id][chipBtn.getAttribute('data-filter-value')] = false;
        }
        state.page = 0;
        fetchAndRender();
      }
    });

    // Add to cart
    container.addEventListener('click', function (event) {
      var atcBtn = event.target && event.target.closest ? event.target.closest('[data-product-search-add-to-cart]') : null;
      if (!atcBtn) return;

      atcBtn.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: atcBtn.getAttribute('data-variant-id'), quantity: 1 })
      }).then(function (response) {
        atcBtn.disabled = false;
        if (response.ok) {
          document.documentElement.dispatchEvent(new CustomEvent('product:added', {
            bubbles: true,
            detail: { productAdded: true, quantity: 1 }
          }));
        }
      }).catch(function () { atcBtn.disabled = false; });
    });

    // Mobile filter drawer open/close (CollectionFilterDrawer itself is only
    // instantiated by the real main-search/main-collection sections, so this
    // is a minimal standalone equivalent against the identical markup).
    if (drawerEl) {
      container.addEventListener('click', function (event) {
        var openBtn = event.target && event.target.closest ? event.target.closest('[data-action="open-drawer"][aria-controls="' + drawerId + '"]') : null;
        if (openBtn) {
          openBtn.setAttribute('aria-expanded', 'true');
          drawerEl.setAttribute('aria-hidden', 'false');
          document.body.classList.add('no-mobile-scroll');
          return;
        }
        var closeBtn = event.target && event.target.closest ? event.target.closest('[data-action="close-drawer"]') : null;
        if (closeBtn && drawerEl.contains(closeBtn)) {
          drawerEl.setAttribute('aria-hidden', 'true');
          document.body.classList.remove('no-mobile-scroll');
          var opener = qs('[data-action="open-drawer"][aria-controls="' + drawerId + '"]', container);
          if (opener) opener.setAttribute('aria-expanded', 'false');
        }
      });
    }

    // Sort value-picker open/close/select (ValuePicker itself is instantiated
    // per-section by the real search/collection pages and can't attach here,
    // so this reimplements the same open/close/select behavior against the
    // identical markup).
    if (sortPicker) {
      var sortToggle = qs('[data-action="open-value-picker"][aria-controls="' + sortId + '"]', container);
      container.addEventListener('click', function (event) {
        var opener = event.target && event.target.closest ? event.target.closest('[data-action="open-value-picker"][aria-controls="' + sortId + '"]') : null;
        if (opener) {
          sortPicker.setAttribute('aria-hidden', 'false');
          opener.setAttribute('aria-expanded', 'true');
          return;
        }
        var closer = event.target && event.target.closest ? event.target.closest('[data-action="close-value-picker"][aria-controls="' + sortId + '"]') : null;
        if (closer) {
          sortPicker.setAttribute('aria-hidden', 'true');
          if (sortToggle) sortToggle.setAttribute('aria-expanded', 'false');
          return;
        }
        var choice = event.target && event.target.closest ? event.target.closest('[data-action="select-value"]') : null;
        if (choice && sortPicker.contains(choice)) {
          state.sort = choice.getAttribute('data-value');
          state.page = 0;
          qsa('[data-action="select-value"]', sortPicker).forEach(function (btn) { btn.classList.toggle('is-selected', btn === choice); });
          if (sortLabelEl) sortLabelEl.textContent = 'Sort by: ' + choice.textContent.trim();
          sortPicker.setAttribute('aria-hidden', 'true');
          if (sortToggle) sortToggle.setAttribute('aria-expanded', 'false');
          fetchAndRender();
        }
      });
    }

    fetchAndRender();
  }

  function removeNativeFallback() {
    // See sections/main-search.liquid: some third-party scripts (observed:
    // Boost's own "boost-sd-ssr" app embed) resurrect <noscript> content into
    // real, visible DOM nodes. Since this script only runs when JS is
    // enabled, it's always correct to strip the no-JS fallback outright.
    qsa('[data-native-search-fallback]').forEach(function (el) { el.remove(); });
  }

  function removeBoostTakeover() {
    // Boost's own client-side widget was observed injecting a second,
    // unstyled results grid directly into #main (outside any theme
    // section), using the same class names as our real collection/search
    // markup (.collection__title, .layout__section, etc.) — duplicating
    // whatever this widget renders. It isn't controlled by anything in this
    // theme; disabling the "boost-sd-ssr" app embed and dashboard toggles
    // didn't stop it, so strip it defensively: any .collection__title not
    // inside a real theme section (data-section-id) is Boost's injection,
    // not ours — remove its whole top-level wrapper.
    var main = document.getElementById('main') || document.body;
    qsa('.collection__title').forEach(function (titleEl) {
      if (titleEl.closest('[data-section-id]')) return;
      var node = titleEl;
      while (node.parentElement && node.parentElement !== main) {
        node = node.parentElement;
      }
      if (node !== main) node.remove();
    });
  }

  function boot() {
    removeNativeFallback();
    removeBoostTakeover();
    qsa('[data-product-search-results]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
